/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import theme from 'common/theme'
import {
  CSSProperties,
  FocusEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

export type TSide = 'top' | 'bottom' | 'left' | 'right'
export type TPosition =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'full'
  | 'center'
  | 'customRight'

interface IPopoverProps {
  dataTestId?: string
  side?: TSide
  position?: TPosition
  children: any
  childrenColor?: string
  full?: boolean
  clickable?: boolean
  onOpenOverlay?: Function
  onCloseOverlay?: Function
  content: React.ReactNode
  wrapperStyle?: CSSProperties
}

const setPosition = ({
  offsetTop,
  offsetLeft,
  offsetHeight,
  offsetWidth,
  side,
  position,
}: {
  offsetTop: any
  offsetLeft: any
  offsetHeight: any
  offsetWidth: any
  side: TSide
  position: TPosition
}) => {
  let top = 0
  let left = 0
  let translateX = 0
  let translateY = 0
  let width = null

  switch (side) {
    case 'bottom':
      top = offsetTop + offsetHeight + 18
      break
    case 'left':
      translateX = -100
      left = offsetLeft - 18
      break
    case 'right':
      left = offsetLeft + offsetWidth + 18
      break
    default:
      translateY = -100
      top = offsetTop - 18
      break
  }
  if (side === 'top' || side === 'bottom') {
    switch (position) {
      case 'left':
        translateX = -100
        left = offsetLeft + offsetWidth / 2 + 30
        break
      case 'right':
        left = offsetLeft + offsetWidth / 2 - 30
        break
      case 'customRight':
        left = offsetLeft + offsetWidth / 2 - 242
        break
      case 'full':
        left = 0
        width = theme.layout.containerWidth
        break
      default:
        translateX = -50
        left = offsetLeft + offsetWidth / 2
        break
    }
  } else {
    switch (position) {
      case 'top':
        translateY = -100
        top = offsetTop + offsetHeight / 2 + 30
        break
      case 'bottom':
        top = offsetTop + offsetHeight / 2 - 30
        break
      default:
        translateY = -50
        top = offsetTop + offsetHeight / 2
        break
    }
  }
  return `
    top: ${top}px;
    left: ${left}px;
    transform: translate(${translateX}%, ${translateY}%);
    ${width !== null ? `width: ${width};` : ''}
  `
}

const setDividerSide = ({ side }: { side: TSide }) => {
  switch (side) {
    case 'bottom':
      return `
        width: 100%;
        height: 18px;
        top: -18px;
        left: 0px;
      `
    case 'left':
      return `
        width: 18px;
        height: 100%;
        right: -17px;
        top: 0px;
      `
    case 'right':
      return `
        width: 18px;
        height: 100%;
        left: -17px;
        top: 0px;
      `
    default:
      return `
        width: 100%;
        height: 18px;
        bottom: -18px;
        left: 0px;
      `
  }
}

const Divider = styled.div`
  position: absolute;
  ${({ side }) => setDividerSide({ side })}
`

const StyledPopover = styled.div`
  ${({
    offsetTop,
    offsetLeft,
    offsetHeight,
    offsetWidth,
    side,
    position,
  }) =>
    setPosition({
      offsetTop,
      offsetLeft,
      offsetHeight,
      offsetWidth,
      side,
      position,
    })}
  position: absolute;
  background-color: ${theme.colors.white};
  border-radius: ${theme.radius.base};
  animation: fadein 0.2s;
  box-shadow: ${theme.popover.boxShadow};
  z-index: ${({ clickable }) =>
    clickable ? theme.zIndex.dropdown : theme.zIndex.popover};
  @keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const StyledContent = styled.div`
  padding: ${({ full }) => (full ? '0' : theme.popover.padding)};
`

const setArrowSide = ({ side }: { side: TSide }) => {
  switch (side) {
    case 'bottom':
      return `
        border-bottom-color: ${theme.colors.white};
        top: -19px;
      `
    case 'right':
      return `
        border-right-color: ${theme.colors.white};
        left: -19px;
      `
    case 'left':
      return `
        border-left-color: ${theme.colors.white};
        right: -19px;
      `
    default:
      return `
        border-top-color: ${theme.colors.white};
        bottom: -19px;
      `
  }
}

const setArrowPosition = ({
  side,
  position,
}: {
  side: TSide
  position: TPosition
}) => {
  if (side === 'top' || side === 'bottom') {
    switch (position) {
      case 'left':
        return 'right: 19px;'
      case 'right':
      case 'full':
        return 'left: 19px;'
      case 'customRight':
        return 'left: 221px;'
      default:
        return 'right: 50%; margin-right: -10px;'
    }
  }
  switch (position) {
    case 'top':
      return 'bottom: 19px;'
    case 'bottom':
      return 'top: 19px;'
    default:
      return 'top: 50%; margin-top: -10px;'
  }
}

const Arrow = styled.i`
  border-color: transparent;
  border-style: solid;
  border-width: 10px;
  position: absolute;
  ${({ side }) => setArrowSide({ side })}
  ${({ side, position }) => setArrowPosition({ side, position })}

  &:after {
    content: ' ';
  }
`

const Children = styled.div`
  ${({ active, color }) =>
    active
      ? `
        > p, > *, > * > p, svg {
          color: ${color};
        }
      `
      : ''}
`

function Popover({
  dataTestId = 'popover',
  side = 'top',
  position = 'center',
  clickable = false,
  children,
  childrenColor = `${theme.colors.orange}`,
  content,
  full,
  onOpenOverlay = () => null,
  onCloseOverlay = () => null,
  wrapperStyle = {},
  ...otherProps
}: IPopoverProps): JSX.Element {
  const [target, setTarget] = useState(null)
  const node = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(false)

  const [offsetTop, setOffsetTop] = useState(
    target ? target.offsetTop : 0,
  )
  const [offsetLeft, setOffsetLeft] = useState(
    target ? target.offsetLeft : 0,
  )
  const [offsetHeight, setOffsetHeight] = useState(
    target ? target.offsetHeight : 0,
  )
  const [offsetWidth, setOffsetWidth] = useState(
    target ? target.offsetWidth : 0,
  )

  const handleOutsideClick = (e) => {
    if (node.current.contains(e.target)) {
      return
    }
    setTarget(null)
  }

  const handleInsideClick = (e) => {
    if (target !== null) {
      setTarget(null)
    } else {
      setTarget(e.currentTarget)
    }
  }

  const handleClose = () => {
    onCloseOverlay()
    setTarget(null)
    setActive(false)
  }

  const handleShow = (
    e: MouseEvent<HTMLDivElement> | FocusEvent<HTMLDivElement>,
  ) => {
    onOpenOverlay()
    setTarget(e.currentTarget)
    setActive(true)
  }

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    setOffsetTop(target ? target.offsetTop : 0)
    setOffsetLeft(target ? target.offsetLeft : 0)
    setOffsetHeight(target ? target.offsetHeight : 0)
    setOffsetWidth(target ? target.offsetWidth : 0)

    if (clickable) {
      document.addEventListener('mousedown', handleOutsideClick)
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick)
      }
    }
  }, [target, clickable])

  return (
    <div
      onMouseLeave={clickable ? null : handleClose}
      data-testid={dataTestId}
      onFocus={handleShow}
      onMouseOver={clickable ? null : handleShow}
      onClick={clickable ? handleInsideClick : null}
      ref={clickable ? node : null}
      style={wrapperStyle}
    >
      {target && (
        <StyledPopover
          offsetTop={offsetTop}
          offsetLeft={offsetLeft}
          offsetHeight={offsetHeight}
          offsetWidth={offsetWidth}
          side={side}
          position={position}
          clickable={clickable}
          {...otherProps}
        >
          <Divider side={side} />
          <Arrow
            side={side}
            position={position}
            data-ralali-id="arrow"
          />
          <StyledContent full={full}>{content}</StyledContent>
        </StyledPopover>
      )}
      <Children active={active} color={childrenColor}>
        {children}
      </Children>
    </div>
  )
}

export default Popover
